// Settings › Destinations (plan step 3.5; Console D's #/settings/alerts without groups, D103):
// where the organization's alerts go: Slack incoming webhooks, signed webhooks, PagerDuty, an
// email address (D39, APS-477: a member's at once, any other once it confirms), and the agent
// handoff's GitHub and Linear destinations (set up in Agents and tokens). Owners and
// admins add, test and delete them; members see them. A URL is sent once and never shown again
// (its host is); a webhook's signing secret is shown once, when created or rotated; a PagerDuty
// integration key is replaced, never shown. The rules are in Alerts (routes/Alerts.tsx).
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useId, useState } from 'react'
import { isApiError, notConfigured } from '../api/client'
import {
  alertDestinationsQuery,
  alertRulesQuery,
  orgQuery,
  useCreateAlertDestination,
  useDeleteAlertDestination,
  useEditAlertAddress,
  useReplaceRoutingKey,
  useRotateAlertSecret,
  useSendAlertConfirmation,
  useTestAlertDestination,
} from '../api/queries'
import type { AlertDestination } from '../api/types'
import { Confirm } from '../components/Confirm'
import { Btn, btnClass, EmptyNote, PageHead, Section, Seg, Status, Table } from '../components/d'
import { Icon } from '../components/icons'
import { SecretField } from '../components/Secret'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import {
  destinationIcon,
  KIND_LABEL,
  PRESET_LABEL,
  ROUTING_KEY,
  ruleStatus,
  ruleSummary,
} from '../lib/alerts'
import { HandoffStatus, isHandoff } from './HandoffSettings'

/** A destination's health: paused after timeouts, failing with its code, or fine. */
function DestinationHealth({ destination: d }: { destination: AlertDestination }) {
  const { t, date } = useI18n()
  const code = d.last_error_code ?? ''
  if (d.paused_until && Date.parse(d.paused_until) > Date.now())
    return (
      <>
        <Status s="mute" label={t('settings.dest.paused')} />
        <span className="t2">
          {t('settings.alerts.paused', {
            time: date(d.paused_until, { dateStyle: 'medium', timeStyle: 'short' }),
          })}{' '}
          <code translate="no">{code}</code>
        </span>
      </>
    )
  if (d.consecutive_failures > 0)
    return (
      <>
        <Status s="fire" label={t('settings.dest.failing')} />
        <span className="t2">
          {t('settings.alerts.failing', { n: d.consecutive_failures, code })}
        </span>
      </>
    )
  if (isHandoff(d)) return <HandoffStatus destination={d} />
  // An email address gets nothing until it confirms, nothing after it unsubscribed, and nothing
  // once its mailbox bounced or its reader marked an alert as spam (APS-477).
  if (d.email?.state === 'pending')
    return <Status s="mute" label={t('settings.alerts.email.pending')} />
  // A bounce is a delivery failing, in the crash tone; an unsubscribe and a complaint are the
  // reader's own choice, in warn (Mosaic).
  const stopped =
    d.email?.state === 'unsubscribed'
      ? {
          s: 'miss' as const,
          label: t('settings.alerts.email.unsubscribed'),
          at: d.email.unsubscribed_at,
        }
      : d.email?.state === 'bounced'
        ? {
            s: 'fire' as const,
            glyph: '×',
            label: t('settings.alerts.email.bounced'),
            at: d.email.stopped_at,
          }
        : d.email?.state === 'complained'
          ? {
              s: 'miss' as const,
              label: t('settings.alerts.email.complained'),
              at: d.email.stopped_at,
            }
          : undefined
  if (stopped)
    return (
      <>
        <Status
          s={stopped.s}
          label={stopped.label}
          {...('glyph' in stopped ? { glyph: stopped.glyph } : {})}
        />
        {stopped.at ? (
          <span className="t2">
            {/* The day, and the year only when it is not this one (Mosaic). */}
            {date(
              stopped.at,
              new Date(stopped.at).getFullYear() === new Date().getFullYear()
                ? { month: 'short', day: 'numeric' }
                : { dateStyle: 'medium' },
            )}
          </span>
        ) : null}
      </>
    )
  return <Status s="ok" />
}

function DestinationForm({ orgId }: { orgId: string }) {
  const { t } = useI18n()
  const create = useCreateAlertDestination(orgId)
  const [kind, setKind] = useState<'slack' | 'webhook' | 'email' | 'pagerduty'>('slack')
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [address, setAddress] = useState('')
  // PagerDuty: the integration key (write-only) and the service's region, instead of a URL.
  const [routingKey, setRoutingKey] = useState('')
  const [region, setRegion] = useState<'us' | 'eu'>('us')
  const ids = {
    title: useId(),
    name: useId(),
    url: useId(),
    help: useId(),
    key: useId(),
    keyHelp: useId(),
    region: useId(),
    address: useId(),
    addressHelp: useId(),
  }
  const pagerDuty = kind === 'pagerduty'
  const email = kind === 'email'
  const ready =
    name.trim() !== '' &&
    (pagerDuty
      ? ROUTING_KEY.test(routingKey.trim())
      : email
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.trim())
        : /^https:\/\//.test(url.trim()))
  const created = create.data?.destination
  return (
    <section className="win" aria-labelledby={ids.title}>
      <div className="win-bar">
        <b id={ids.title}>{t('settings.alerts.addDestination')}</b>
      </div>
      <form
        className="se-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!ready) return
          create.mutate(
            kind === 'pagerduty'
              ? { kind, name: name.trim(), routing_key: routingKey.trim(), region }
              : kind === 'email'
                ? { kind, name: name.trim(), address: address.trim() }
                : { kind, name: name.trim(), url: url.trim() },
            {
              onSuccess: () => {
                setName('')
                setUrl('')
                setRoutingKey('')
                setAddress('')
              },
            },
          )
        }}
      >
        <div className="lbl">
          {t('settings.alerts.kind')}
          <Seg
            label={t('settings.alerts.kind')}
            options={[
              { value: 'slack', label: t('settings.alerts.kind.slack') },
              { value: 'webhook', label: t('settings.alerts.kind.webhook') },
              { value: 'email', label: t('settings.alerts.kind.email') },
              { value: 'pagerduty', label: t('settings.alerts.kind.pagerduty') },
            ]}
            value={kind}
            onChange={setKind}
          />
        </div>
        <label className="lbl" htmlFor={ids.name}>
          {t('settings.alerts.name')}
          <input
            id={ids.name}
            className="input"
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {email ? (
          <label className="lbl" htmlFor={ids.address}>
            {t('settings.alerts.address')}
            <input
              id={ids.address}
              className="input"
              type="email"
              autoComplete="off"
              spellCheck={false}
              maxLength={254}
              aria-describedby={ids.addressHelp}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <span id={ids.addressHelp} className="se-hint">
              {t('settings.alerts.addressHint')}
            </span>
          </label>
        ) : pagerDuty ? (
          <>
            <label className="lbl" htmlFor={ids.key}>
              {t('settings.alerts.routingKey')}
              <input
                id={ids.key}
                className="input mono"
                type="password"
                translate="no"
                autoComplete="off"
                spellCheck={false}
                maxLength={32}
                aria-describedby={ids.keyHelp}
                value={routingKey}
                onChange={(e) => setRoutingKey(e.target.value)}
              />
              <span id={ids.keyHelp} className="se-hint">
                {t('settings.alerts.routingKeyHint')}
              </span>
            </label>
            <label className="lbl" htmlFor={ids.region}>
              {t('settings.alerts.region')}
              <select
                id={ids.region}
                className="select"
                value={region}
                onChange={(e) => setRegion(e.target.value as 'us' | 'eu')}
              >
                <option value="us">{t('settings.alerts.region.us')}</option>
                <option value="eu">{t('settings.alerts.region.eu')}</option>
              </select>
            </label>
            <p className="se-hint">{t('settings.alerts.pagerDutyTestNote')}</p>
          </>
        ) : (
          <label className="lbl" htmlFor={ids.url}>
            {t('settings.alerts.url')}
            <input
              id={ids.url}
              className="input mono"
              type="url"
              translate="no"
              autoComplete="off"
              spellCheck={false}
              maxLength={2048}
              aria-describedby={ids.help}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <span id={ids.help} className="se-hint">
              {kind === 'slack'
                ? t('settings.alerts.urlHint.slack')
                : t('settings.alerts.urlHint.webhook')}
            </span>
          </label>
        )}
        <div className="se-row">
          <Btn type="submit" variant="solid" disabled={!ready || create.isPending}>
            {t('settings.alerts.addDestination')}
          </Btn>
        </div>
        {create.data?.secret ? (
          <div role="status" className="se-hint">
            <p>{t('settings.alerts.secretOnce')}</p>
            <SecretField label={t('settings.alerts.secretLabel')} value={create.data.secret} />
          </div>
        ) : created?.email?.state === 'pending' ? (
          <p role="status" className="se-hint">
            {t('settings.alerts.confirmationSent', { address: created.email.address })}
          </p>
        ) : create.isSuccess ? (
          <p role="status" className="se-hint">
            <Status s="ok" label={t('settings.alerts.added')} />
          </p>
        ) : null}
        {create.error ? (
          <p role="alert" className="se-hint c-crash">
            {isApiError(create.error, 400)
              ? pagerDuty
                ? t('settings.alerts.routingKeyRefused')
                : email
                  ? t('settings.alerts.addressRefused')
                  : t('settings.alerts.urlRefused')
              : isApiError(create.error, 429)
                ? t('settings.alerts.confirmTooMany')
                : isApiError(create.error, 403)
                  ? t('settings.adminOnly')
                  : isApiError(create.error, 402)
                    ? t('settings.alerts.tooManyDestinations')
                    : t('state.failed')}
          </p>
        ) : null}
      </form>
    </section>
  )
}

/** A new address for an email destination whose mailbox bounced (APS-477). */
function EditAddress({ orgId, destination }: { orgId: string; destination: AlertDestination }) {
  const { t } = useI18n()
  const edit = useEditAlertAddress(orgId)
  const [open, setOpen] = useState(false)
  const [address, setAddress] = useState(destination.email?.address ?? '')
  const id = useId()
  const old = destination.email?.address
  if (!open)
    return (
      <Btn size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {t('settings.alerts.editAddress')}
      </Btn>
    )
  const next = address.trim()
  const ready = next.includes('@') && next.toLowerCase() !== old
  return (
    <form
      className="al-inline"
      aria-label={t('settings.alerts.editAddress')}
      onSubmit={(e) => {
        e.preventDefault()
        if (!ready) return
        edit.mutate(
          {
            destinationId: destination.id,
            address: next,
            ...(destination.name === old ? { name: next.toLowerCase() } : {}),
          },
          { onSuccess: () => setOpen(false) },
        )
      }}
    >
      <label htmlFor={id} className="visually-hidden">
        {t('settings.alerts.address')}
      </label>
      <input
        id={id}
        className="input mono al-addr"
        type="email"
        translate="no"
        autoComplete="off"
        spellCheck={false}
        maxLength={254}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
      <Btn type="submit" size="sm" disabled={!ready || edit.isPending}>
        {t('settings.alerts.saveAddress')}
      </Btn>
      <Btn size="sm" variant="ghost" onClick={() => setOpen(false)}>
        {t('settings.cancel')}
      </Btn>
      {edit.error ? (
        <span role="alert" className="se-hint c-crash">
          {isApiError(edit.error, 400)
            ? t('settings.alerts.addressRefused')
            : isApiError(edit.error, 429)
              ? t('settings.alerts.confirmTooMany')
              : t('state.failed')}
        </span>
      ) : null}
    </form>
  )
}

/** A new integration key for a PagerDuty destination; the current one is never shown. */
function ReplaceRoutingKey({
  orgId,
  destination,
}: {
  orgId: string
  destination: AlertDestination
}) {
  const { t } = useI18n()
  const replace = useReplaceRoutingKey(orgId)
  const [open, setOpen] = useState(false)
  const [key, setKey] = useState('')
  const id = useId()
  if (!open)
    return (
      <>
        <Btn size="sm" onClick={() => setOpen(true)}>
          {t('settings.alerts.replaceKey')}
        </Btn>
        {replace.isSuccess ? (
          <span role="status" className="se-hint">
            {t('settings.alerts.keyReplaced')}
          </span>
        ) : null}
      </>
    )
  const ready = ROUTING_KEY.test(key.trim())
  return (
    <form
      className="al-inline"
      aria-label={t('settings.alerts.replaceKey')}
      onSubmit={(e) => {
        e.preventDefault()
        if (!ready) return
        replace.mutate(
          { destinationId: destination.id, routingKey: key.trim() },
          {
            onSuccess: () => {
              setKey('')
              setOpen(false)
            },
          },
        )
      }}
    >
      <label htmlFor={id} className="visually-hidden">
        {t('settings.alerts.routingKey')}
      </label>
      <input
        id={id}
        className="input mono"
        type="password"
        translate="no"
        autoComplete="off"
        spellCheck={false}
        maxLength={32}
        value={key}
        onChange={(e) => setKey(e.target.value)}
      />
      <Btn type="submit" size="sm" disabled={!ready || replace.isPending}>
        {t('settings.alerts.replaceKey')}
      </Btn>
      <Btn size="sm" variant="ghost" onClick={() => setOpen(false)}>
        {t('settings.cancel')}
      </Btn>
      {replace.error ? (
        <span role="alert" className="se-hint c-crash">
          {isApiError(replace.error, 400)
            ? t('settings.alerts.routingKeyRefused')
            : t('state.failed')}
        </span>
      ) : null}
    </form>
  )
}

/** The organization's destinations: name, kind, host, health, last sent, and their actions. */
export function DestinationsPage({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: ReactNode
  title: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const org = useQuery(orgQuery(orgId))
  const canEdit = org.data?.role === 'owner' || org.data?.role === 'admin'
  const list = useQuery(alertDestinationsQuery(orgId))
  const rules = useQuery(alertRulesQuery(orgId, projectId))
  const test = useTestAlertDestination(orgId)
  const rotate = useRotateAlertSecret(orgId)
  const remove = useDeleteAlertDestination(orgId)
  const resend = useSendAlertConfirmation(orgId)
  const rows = list.data?.destinations ?? []
  // A test to PagerDuty triggers and resolves an incident: say so with its result.
  const testedKind = rows.find((d) => d.id === test.variables)?.kind
  const testedPagerDuty = testedKind === 'pagerduty'
  const params = { orgId, projectId }
  const now = Date.now()
  const off = list.isError && notConfigured(list.error)
  const meta =
    org.data && list.data
      ? t('settings.dest.meta', { org: org.data.name, count: rows.length })
      : undefined
  const sending = (rules.data?.rules ?? []).filter((r) => r.destination_id !== null)

  return (
    <main id="main" className="pg se-pg alerts">
      <PageHead
        crumb={crumb}
        title={title}
        meta={meta}
        actions={
          <Link className={btnClass()} to="/o/$orgId/p/$projectId/alerts" params={params}>
            <Icon name="alerts" className="se-i" />
            {t('settings.dest.rules')}
          </Link>
        }
      />
      {list.isPending || org.isPending ? (
        <Loading />
      ) : off ? (
        <EmptyNote title={t('settings.alerts.off')} />
      ) : list.isError ? (
        <ErrorState error={list.error} retry={() => list.refetch()} />
      ) : (
        <>
          <Section
            title={t('settings.dest.title')}
            meta={
              <span title={t('settings.alerts.destinationsSub')}>
                {t('settings.dest.ofMax', { n: rows.length })}
              </span>
            }
          >
            {rows.length === 0 ? (
              <EmptyNote title={t('settings.alerts.noDestinations')} />
            ) : (
              <Table label={t('table.of', { name: t('settings.dest.title') })} className="al-dests">
                <thead>
                  <tr>
                    <th scope="col">{t('settings.dest.col.destination')}</th>
                    <th scope="col" className="al-wide">
                      {t('settings.alerts.kind')}
                    </th>
                    <th scope="col" className="al-wide">
                      {t('settings.alerts.host')}
                    </th>
                    <th scope="col">{t('settings.alerts.status')}</th>
                    <th scope="col" className="al-wide">
                      {t('settings.dest.col.lastSent')}
                    </th>
                    <th scope="col">
                      <span className="visually-hidden">{t('settings.keys.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.id}>
                      <td className="al-who">
                        <span className="se-who">
                          <Icon name={destinationIcon(d)} className="se-i" />
                          {d.name}
                        </span>
                        {/* An email destination named otherwise: its address under the name. */}
                        {d.email && d.email.address !== d.name ? (
                          <span className="t2" translate="no">
                            {d.email.address}
                          </span>
                        ) : null}
                        {/* On a phone the kind and the host fold under the name (Mosaic). */}
                        <span className="t2 al-narrow">{`${t(KIND_LABEL[d.kind])} · ${d.host}`}</span>
                      </td>
                      <td className="al-wide">{t(KIND_LABEL[d.kind])}</td>
                      <td className="al-wide">
                        <code translate="no">{d.host}</code>
                      </td>
                      <td className="wrap">
                        <DestinationHealth destination={d} />
                      </td>
                      <td className="al-wide">
                        {d.last_delivered_at ? (
                          i18n.date(d.last_delivered_at, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        ) : (
                          <span className="faint">{t('settings.dest.never')}</span>
                        )}
                      </td>
                      <td className="al-acts">
                        {canEdit ? (
                          <>
                            {isHandoff(d) ? (
                              <Link
                                className={btnClass('ghost', 'sm')}
                                to="/o/$orgId/p/$projectId/settings/$section"
                                params={{ ...params, section: 'agents' }}
                              >
                                {t('settings.alerts.handoff')}
                              </Link>
                            ) : null}
                            {d.email?.state === 'bounced' ? (
                              // The same address would bounce again: a new one instead (Mosaic).
                              <EditAddress orgId={orgId} destination={d} />
                            ) : d.email && d.email.state !== 'confirmed' ? (
                              <Btn
                                size="sm"
                                disabled={resend.isPending}
                                onClick={() => resend.mutate(d.id)}
                              >
                                {t('settings.alerts.sendConfirmation')}
                              </Btn>
                            ) : d.kind === 'pagerduty' ? (
                              <Confirm
                                label={t('settings.alerts.test')}
                                buttonClass="btn sm"
                                buttonName={`${t('settings.alerts.test')} ${d.name}`}
                                busy={test.isPending}
                                title={t('settings.alerts.testPagerDutyTitle')}
                                body={t('settings.alerts.testPagerDutyBody', { name: d.name })}
                                confirm={t('settings.alerts.testPagerDutyConfirm')}
                                confirmClass="btn solid"
                                onConfirm={() => test.mutate(d.id)}
                              />
                            ) : (
                              <Btn
                                size="sm"
                                disabled={test.isPending}
                                onClick={() => test.mutate(d.id)}
                              >
                                {t('settings.alerts.test')}
                              </Btn>
                            )}
                            {d.kind === 'pagerduty' ? (
                              <ReplaceRoutingKey orgId={orgId} destination={d} />
                            ) : null}
                            {d.kind === 'webhook' ? (
                              <Confirm
                                label={t('settings.alerts.rotate')}
                                title={t('settings.alerts.rotateTitle')}
                                body={t('settings.alerts.rotateBody')}
                                confirm={t('settings.alerts.rotate')}
                                busy={rotate.isPending}
                                buttonClass="btn danger sm"
                                onConfirm={() => rotate.mutate(d.id)}
                              />
                            ) : null}
                            <Confirm
                              label={t('settings.alerts.delete')}
                              title={t('settings.alerts.deleteDestinationTitle')}
                              body={t('settings.alerts.deleteDestinationBody')}
                              confirm={t('settings.alerts.delete')}
                              busy={remove.isPending}
                              buttonClass="btn danger sm al-del"
                              onConfirm={() => remove.mutate(d.id)}
                            />
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {test.data ? (
              <p role="status" className="se-hint al-note">
                {test.data.delivery === 'sent'
                  ? testedPagerDuty
                    ? t('settings.alerts.testSentPagerDuty')
                    : testedKind === 'email'
                      ? t('settings.alerts.testSentEmail')
                      : t('settings.alerts.testSent')
                  : t('settings.alerts.testFailed', { code: test.data.error_code ?? '' })}
              </p>
            ) : null}
            {test.error ? (
              <p role="alert" className="se-hint al-note c-crash">
                {isApiError(test.error, 429)
                  ? t('settings.alerts.testTooMany')
                  : isApiError(test.error, 409)
                    ? t('settings.alerts.testUnconfirmed')
                    : t('state.failed')}
              </p>
            ) : null}
            {resend.isSuccess ? (
              <p role="status" className="se-hint al-note">
                {t('settings.alerts.confirmationResent')}
              </p>
            ) : null}
            {resend.error ? (
              <p role="alert" className="se-hint al-note c-crash">
                {isApiError(resend.error, 429)
                  ? t('settings.alerts.confirmTooMany')
                  : t('state.failed')}
              </p>
            ) : null}
            {rotate.data ? (
              <div role="status" className="se-hint al-note">
                <p>{t('settings.alerts.secretOnce')}</p>
                <SecretField label={t('settings.alerts.secretLabel')} value={rotate.data.secret} />
              </div>
            ) : null}
            <p className="se-hint al-note">{t('settings.alerts.deleteDestinationBody')}</p>
          </Section>
          <div className="grid2">
            <Section
              title={t('settings.dest.sending')}
              right={
                <Link
                  className={btnClass('ghost', 'sm')}
                  to="/o/$orgId/p/$projectId/alerts"
                  params={params}
                >
                  {t('settings.dest.openAlerts')}
                </Link>
              }
            >
              {rules.isPending ? (
                <Loading />
              ) : sending.length === 0 ? (
                <EmptyNote title={t('settings.alerts.noRules')} />
              ) : (
                <ul className="rl al-rl al-rl-st">
                  {sending.map((r) => {
                    const s = ruleStatus(r, now, i18n)
                    const d = rows.find((x) => x.id === r.destination_id)
                    return (
                      <li key={r.id}>
                        <span>
                          <Link
                            className="ul"
                            to="/o/$orgId/p/$projectId/alerts/$ruleId"
                            params={{ ...params, ruleId: r.id }}
                          >
                            {t(PRESET_LABEL[r.preset])}
                          </Link>
                          <span className="t2">
                            {ruleSummary(r, i18n)}
                            {d ? ` → ${d.name}` : ''}
                          </span>
                        </span>
                        <Status s={s.s} label={s.label} />
                      </li>
                    )
                  })}
                </ul>
              )}
            </Section>
            {canEdit ? (
              <DestinationForm orgId={orgId} />
            ) : (
              <p className="se-hint">{t('settings.adminOnly')}</p>
            )}
          </div>
        </>
      )}
    </main>
  )
}
